import AnnouncementDialog from "./AnnouncementDialog";
import HelpCallout from "./HelpCallout";
import HelpVideoDialog from "./HelpVideoDialog";
import { findHelpAnchor, waitForHelpAnchor } from "./anchors";
import { ANNOUNCEMENTS } from "./announcements";
import { helpMedia } from "./helpMedia";
import { Announcement, HintDefinition, helpKeys } from "./registry";
import { useCapabilities } from "contexts/CapabilitiesContext";
import {
  HelpEvent,
  useRecordHelpProgressMutation,
  useViewerHelpProgressQuery,
} from "generated.hooks";
import useAuth from "hooks/useAuth";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { matchPath, useLocation, useSearchParams } from "react-router-dom";

/** Pages where the what's-new dialog never opens on its own. */
const QUIET_PATHS = ["/plans", "/help"];

/**
 * How long a page is on screen before the what's-new dialog opens over it,
 * so the user first sees where they are, then what is new there.
 */
export const ANNOUNCEMENT_DELAY_MS = 3000;

const EVENT_FIELDS = {
  [HelpEvent.Seen]: "seenAt",
  [HelpEvent.Played]: "playedAt",
  [HelpEvent.Completed]: "completedAt",
  [HelpEvent.Dismissed]: "dismissedAt",
} as const;

interface ProgressRow {
  key: string;
  seenAt?: string | null;
  playedAt?: string | null;
  completedAt?: string | null;
  dismissedAt?: string | null;
}

interface HelpContextValue {
  /** Announcements this viewer can see (their `requires` met), oldest first. */
  announcements: Announcement[];
  isSeen: (key: string) => boolean;
  /** New to this viewer: they joined before its release and have not seen it. */
  isNew: (announcement: Announcement) => boolean;
  /** An announcement the viewer qualifies for has not been seen yet. */
  hasUnseen: boolean;
  record: (key: string, event: HelpEvent) => void;
  playVideo: (announcement: Announcement) => void;
  startTour: (announcement: Announcement) => void;
}

const DISABLED: HelpContextValue = {
  announcements: [],
  isSeen: () => false,
  isNew: () => false,
  hasUnseen: false,
  record: () => {},
  playVideo: () => {},
  startTour: () => {},
};

const HelpContext = createContext<HelpContextValue>(DISABLED);

/** Joined before the release day, so the feature was new to them. */
const joinedBefore = (createdAt: string | undefined, released: string) =>
  !!createdAt && new Date(createdAt) < new Date(`${released}T00:00:00Z`);

// Only a dialog on screen counts. Closed drawers stay mounted on phones
// (the menu, the dashboard's bottom sheets) with their role="dialog" panel
// hidden; counting those kept every hint off phones.
const anotherDialogIsOpen = () =>
  Array.from(document.querySelectorAll<HTMLElement>('[role="dialog"]')).some(
    (element) =>
      !element.closest('[aria-hidden="true"]') &&
      getComputedStyle(element).visibility !== "hidden"
  );

interface TourState {
  announcement: Announcement;
  index: number;
  /** null: the step's anchor is missing and it falls back to the centre. */
  anchor: HTMLElement | null;
}

interface HintState {
  announcement: Announcement;
  hint: HintDefinition;
  anchor: HTMLElement;
}

/**
 * In-app help for the signed-in app shell: the what's-new dialog, its video,
 * tours and hints, and the seen-state behind them (viewerHelpProgress).
 * Superusers get none of it.
 */
function HelpProvider({
  children,
  announcements: registered = ANNOUNCEMENTS,
  announcementDelayMs = ANNOUNCEMENT_DELAY_MS,
}: {
  children: React.ReactNode;
  announcements?: Announcement[];
  announcementDelayMs?: number;
}) {
  const { isAuthenticated, isSuperuser } = useAuth();
  const enabled = isAuthenticated && !isSuperuser;
  const { hasCapability } = useCapabilities();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { data } = useViewerHelpProgressQuery({ skip: !enabled });
  const [recordMutation] = useRecordHelpProgressMutation();
  // Recorded this session, ahead of (and then replaced by) the server's row.
  const [local, setLocal] = useState<Record<string, ProgressRow>>({});

  const [dialogId, setDialogIdState] = useState<string | null>(null);
  // The dialog about to open, set in the same tick as the state. Effects in
  // one commit see the old state; this stops a hint opening under a dialog.
  const dialogIdRef = useRef<string | null>(null);
  const setDialogId = useCallback((id: string | null) => {
    dialogIdRef.current = id;
    setDialogIdState(id);
  }, []);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [tour, setTour] = useState<TourState | null>(null);
  const [hint, setHint] = useState<HintState | null>(null);
  // At most one announcement opens by itself per page load.
  const autoShown = useRef(false);
  const forcedId = useRef<string | null>(null);
  // When the current page appeared, and whether a dialog is waiting out its
  // delay (hints hold back meanwhile, or the dialog would cover one).
  const arrivedAt = useRef(Date.now());
  const dialogPending = useRef(false);
  const tourRun = useRef(0);

  const loaded = enabled && !!data;
  const joined = data?.userMe?.createdAt;

  const progress = useMemo(() => {
    const rows: Record<string, ProgressRow> = {};
    (data?.viewerHelpProgress ?? []).forEach((row) => {
      rows[row.key] = row;
    });
    return { ...rows, ...local };
  }, [data, local]);

  const isSeen = useCallback(
    (key: string) => !!progress[key]?.seenAt,
    [progress]
  );

  const record = useCallback(
    (key: string, event: HelpEvent) => {
      if (!enabled) {
        return;
      }
      const field = EVENT_FIELDS[event];
      // Only the first time counts, so a repeat (every resume of a video
      // fires onPlay) needs no request.
      if (progress[key]?.[field]) {
        return;
      }
      setLocal((rows) => {
        const row = rows[key] ?? progress[key] ?? { key };
        if (row[field]) {
          return rows;
        }
        return {
          ...rows,
          [key]: { ...row, [field]: new Date().toISOString() },
        };
      });
      recordMutation({ variables: { key, event } })
        .then(({ data: result }) => {
          const payload = result?.recordHelpProgress;
          if (payload?.ok && payload.progress) {
            const row = payload.progress;
            setLocal((rows) => ({ ...rows, [row.key]: row }));
          } else {
            console.error(
              "Recording help progress was refused",
              key,
              payload?.errors
            );
          }
        })
        // Seen-state is a convenience: a failure means a dialog may show
        // again, which is not worth interrupting the user over.
        .catch((error) =>
          console.error("Recording help progress failed", key, error)
        );
    },
    [enabled, progress, recordMutation]
  );

  const announcements = useMemo(
    () =>
      registered
        .filter((a) => !a.requires || hasCapability(a.requires.capability))
        .sort((a, b) => a.released.localeCompare(b.released)),
    [registered, hasCapability]
  );

  const qualifying = useMemo(
    () => announcements.filter((a) => joinedBefore(joined, a.released)),
    [announcements, joined]
  );

  const isNew = useCallback(
    (a: Announcement) =>
      joinedBefore(joined, a.released) && !isSeen(helpKeys.announce(a.id)),
    [joined, isSeen]
  );

  const hasUnseen = qualifying.some(isNew);

  const byId = (id: string | null) =>
    registered.find((a) => a.id === id) ?? null;

  useEffect(() => {
    arrivedAt.current = Date.now();
  }, [pathname]);

  // Time left before a dialog may open on this page. Counted from arrival, so
  // a slow help query does not add the whole delay again.
  const remainingDelay = useCallback(
    () => Math.max(0, announcementDelayMs - (Date.now() - arrivedAt.current)),
    [announcementDelayMs]
  );

  // The what's-new dialog: the oldest unseen announcement, once per load,
  // after the page has been on screen for a moment. Leaving the page first
  // cancels it.
  useEffect(() => {
    if (!loaded || autoShown.current || dialogId || tour) {
      return undefined;
    }
    if (QUIET_PATHS.some((path) => pathname.startsWith(path))) {
      return undefined;
    }
    const next = qualifying.find(
      (a) =>
        !isSeen(helpKeys.announce(a.id)) &&
        (!a.route || matchPath(a.route, pathname))
    );
    if (!next) {
      return undefined;
    }
    const open = () => {
      if (anotherDialogIsOpen()) {
        return false;
      }
      autoShown.current = true;
      setDialogId(next.id);
      return true;
    };
    dialogPending.current = true;
    let poll: number | undefined;
    const wait = window.setTimeout(() => {
      if (!open()) {
        poll = window.setInterval(() => {
          if (open()) {
            window.clearInterval(poll);
          }
        }, 1000);
      }
    }, remainingDelay());
    return () => {
      window.clearTimeout(wait);
      window.clearInterval(poll);
      dialogPending.current = false;
    };
  }, [
    loaded,
    dialogId,
    tour,
    pathname,
    qualifying,
    isSeen,
    setDialogId,
    remainingDelay,
  ]);

  // ?help=<id> shows an announcement whatever its seen-state.
  const forced = searchParams.get("help");
  useEffect(() => {
    if (!enabled || !forced || forcedId.current === forced || !byId(forced)) {
      return undefined;
    }
    autoShown.current = true;
    dialogPending.current = true;
    const wait = window.setTimeout(() => {
      // Marked handled only once shown, so a re-run before then reschedules.
      forcedId.current = forced;
      dialogPending.current = false;
      setTour(null);
      setDialogId(forced);
    }, remainingDelay());
    return () => {
      window.clearTimeout(wait);
      dialogPending.current = false;
    };
    // Not byId: it reads only the registry prop, which never changes.
  }, [enabled, forced, setDialogId, remainingDelay]);

  // Hints: the first time a hint's anchor is on its page.
  useEffect(() => {
    if (!loaded || dialogId || playerId || tour || hint) {
      return undefined;
    }
    const candidates = announcements.flatMap((a) =>
      (a.hints ?? [])
        .filter(
          (h) =>
            matchPath(h.route, pathname) &&
            !isSeen(helpKeys.hint(a.id, h.anchor))
        )
        .map((h) => ({ announcement: a, hint: h }))
    );
    if (!candidates.length) {
      return undefined;
    }
    const show = () => {
      for (const candidate of candidates) {
        const anchor = findHelpAnchor(candidate.hint.anchor);
        if (
          anchor &&
          !dialogIdRef.current &&
          !dialogPending.current &&
          !anotherDialogIsOpen()
        ) {
          setHint({ ...candidate, anchor });
          record(
            helpKeys.hint(candidate.announcement.id, candidate.hint.anchor),
            HelpEvent.Seen
          );
          return true;
        }
      }
      return false;
    };
    if (show()) {
      return undefined;
    }
    // The anchor may come later: the LIVE badge appears only once the
    // device is broadcasting.
    const timer = window.setInterval(() => {
      if (show()) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [
    loaded,
    dialogId,
    playerId,
    tour,
    hint,
    announcements,
    pathname,
    isSeen,
    record,
  ]);

  // A hint belongs to its page.
  useEffect(() => {
    setHint((current) =>
      current && !matchPath(current.hint.route, pathname) ? null : current
    );
  }, [pathname]);

  const goToStep = useCallback(
    async (announcement: Announcement, index: number) => {
      const run = ++tourRun.current;
      const steps = announcement.tour ?? [];
      for (let i = index; i < steps.length; i += 1) {
        const step = steps[i];
        // eslint-disable-next-line no-await-in-loop
        await step.before?.();
        // eslint-disable-next-line no-await-in-loop
        const anchor = await waitForHelpAnchor(step.anchor);
        if (run !== tourRun.current) {
          return;
        }
        if (anchor || step.fallback === "center") {
          setTour({ announcement, index: i, anchor });
          return;
        }
      }
      // Every remaining step's anchor is missing: never point at empty space.
      setTour(null);
    },
    []
  );

  const startTour = useCallback(
    (announcement: Announcement) => {
      if (!announcement.tour?.length) {
        return;
      }
      setDialogId(null);
      setHint(null);
      record(helpKeys.tour(announcement.id), HelpEvent.Seen);
      goToStep(announcement, 0);
    },
    [goToStep, record, setDialogId]
  );

  const nextStep = useCallback(() => {
    if (!tour) {
      return;
    }
    const last = tour.index >= (tour.announcement.tour?.length ?? 0) - 1;
    if (last) {
      record(helpKeys.tour(tour.announcement.id), HelpEvent.Completed);
      tourRun.current += 1;
      setTour(null);
    } else {
      goToStep(tour.announcement, tour.index + 1);
    }
  }, [tour, goToStep, record]);

  const skipTour = useCallback(() => {
    if (tour) {
      record(helpKeys.tour(tour.announcement.id), HelpEvent.Dismissed);
    }
    tourRun.current += 1;
    setTour(null);
  }, [tour, record]);

  const closeHint = useCallback(() => setHint(null), []);

  const playVideo = useCallback(
    (announcement: Announcement) => {
      // Watching counts as seeing it, wherever it was opened from.
      record(helpKeys.announce(announcement.id), HelpEvent.Seen);
      setPlayerId(announcement.id);
    },
    [record]
  );

  const dialogAnnouncement = byId(dialogId);
  useEffect(() => {
    if (dialogAnnouncement) {
      record(helpKeys.announce(dialogAnnouncement.id), HelpEvent.Seen);
    }
    // Once per opening: not `record`, which changes with every row.
  }, [dialogAnnouncement?.id]);

  const playerAnnouncement = byId(playerId);
  const playerMedia = playerAnnouncement
    ? helpMedia(playerAnnouncement.id)
    : null;
  const tourStep = tour?.announcement.tour?.[tour.index];

  const value = useMemo(
    () =>
      enabled
        ? {
            announcements,
            isSeen,
            isNew,
            hasUnseen,
            record,
            playVideo,
            startTour,
          }
        : DISABLED,
    [
      enabled,
      announcements,
      isSeen,
      isNew,
      hasUnseen,
      record,
      playVideo,
      startTour,
    ]
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      {dialogAnnouncement && (
        <AnnouncementDialog
          announcement={dialogAnnouncement}
          onPlay={() => playVideo(dialogAnnouncement)}
          onShowMe={() => startTour(dialogAnnouncement)}
          onClose={() => {
            record(
              helpKeys.announce(dialogAnnouncement.id),
              HelpEvent.Dismissed
            );
            setDialogId(null);
          }}
        />
      )}
      {playerAnnouncement && playerMedia && (
        <HelpVideoDialog
          title={playerAnnouncement.title}
          media={playerMedia}
          onClose={() => setPlayerId(null)}
          onPlay={() =>
            record(helpKeys.announce(playerAnnouncement.id), HelpEvent.Played)
          }
          onEnded={() =>
            record(
              helpKeys.announce(playerAnnouncement.id),
              HelpEvent.Completed
            )
          }
        />
      )}
      {tour && tourStep && (
        <HelpCallout
          key={`${tour.announcement.id}-${tour.index}`}
          anchor={tour.anchor}
          title={tourStep.title}
          body={tourStep.body}
          placement={tourStep.placement}
          modal
          step={{
            index: tour.index,
            total: tour.announcement.tour?.length ?? 0,
          }}
          primaryLabel={
            tour.index >= (tour.announcement.tour?.length ?? 0) - 1
              ? "Done"
              : "Next"
          }
          onPrimary={nextStep}
          onSkip={skipTour}
          onAnchorClick={nextStep}
          onAnchorLost={skipTour}
        />
      )}
      {hint && !tour && (
        <HelpCallout
          anchor={hint.anchor}
          title={hint.hint.title}
          body={hint.hint.body}
          placement={hint.hint.placement}
          modal={false}
          primaryLabel="Got it"
          onPrimary={closeHint}
          closeOnAnyPress
          onAnchorLost={closeHint}
        />
      )}
    </HelpContext.Provider>
  );
}

const useHelp = (): HelpContextValue => useContext(HelpContext);

export { HelpProvider, useHelp };
